(() => {
  if (window.__mbCollectionQuickViewUpgradeReady) return;
  window.__mbCollectionQuickViewUpgradeReady = true;

  const pageCache = new Map();
  const TARGET_TEXT = 'tabla de medidas';
  let activeSizeGuideOpener = null;
  let requestSequence = 0;

  const normalizeText = (value) =>
    String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLocaleLowerCase('es-AR');

  const parseDocument = (markup) =>
    new DOMParser().parseFromString(markup || '', 'text/html');

  const getProductPageUrl = (quickViewUrl) => {
    const url = new URL(quickViewUrl, window.location.origin);
    url.searchParams.delete('view');
    return url.toString();
  };

  const fetchProductPage = async (url) => {
    if (pageCache.has(url)) return pageCache.get(url);

    const promise = fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Product page request failed (${response.status})`);
      return response.text();
    });

    pageCache.set(url, promise);
    try {
      return await promise;
    } catch (error) {
      pageCache.delete(url);
      throw error;
    }
  };

  const waitForNativeQuickView = (container, timeout = 12000) =>
    new Promise((resolve, reject) => {
      const ready = () =>
        container.querySelector('.product_details_info, [id^="ProductInfo-"]');

      if (ready()) {
        resolve();
        return;
      }

      const observer = new MutationObserver(() => {
        if (!ready()) return;
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      });

      observer.observe(container, { childList: true, subtree: true });
      const timer = window.setTimeout(() => {
        observer.disconnect();
        reject(new Error('Native quick view did not finish loading'));
      }, timeout);
    });

  const findMeasureTarget = (description) => {
    if (!description) return null;

    const walker = document.createTreeWalker(description, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, noscript')) {
          return NodeFilter.FILTER_REJECT;
        }
        return normalizeText(node.nodeValue).includes(TARGET_TEXT)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    });

    const textNode = walker.nextNode();
    if (!textNode?.parentElement) return null;
    return (
      textNode.parentElement.closest('h1, h2, h3, h4, h5, h6, p, li, tr, div') ||
      textNode.parentElement
    );
  };

  const scrollInsideQuickView = (modal, target) => {
    const scroller = modal?.querySelector('.quickview-block');
    if (!scroller || !target) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = scroller.scrollTop + targetRect.top - scrollerRect.top - 72;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    scroller.scrollTo({
      top: Math.max(0, top),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  const buildDescription = (sourceDocument) => {
    const sourceDescription = sourceDocument.querySelector(
      '.product-description-tab [data-mb-product-description], .product-description-tab .product-description'
    );
    if (!sourceDescription) return null;

    const section = document.createElement('section');
    section.className = 'mb-collection-quickview__description';
    section.setAttribute('data-mb-collection-product-description', '');
    section.innerHTML = `
      <h3 class="mb-collection-quickview__description-title">Detalles del producto</h3>
      <div class="mb-collection-quickview__description-body rte" data-mb-product-description></div>
    `;
    section.querySelector('[data-mb-product-description]').innerHTML = sourceDescription.innerHTML;
    return section;
  };

  const buildSizeGuide = (sourceDocument, modal) => {
    const sourceModal = sourceDocument.querySelector('#size-modal');
    const sourceContent = sourceModal?.querySelector('.modal-content');
    if (!sourceContent) return null;

    const guide = document.createElement('div');
    guide.className = 'mb-collection-quickview__size-guide';
    guide.setAttribute('data-mb-collection-size-guide', '');
    guide.setAttribute('hidden', '');
    guide.setAttribute('aria-hidden', 'true');

    const suffix = String(modal.id || 'quickview').replace(/[^a-zA-Z0-9_-]/g, '-');
    const titleId = `mb-collection-size-guide-title-${suffix}`;
    const panelId = `mb-collection-size-guide-panel-${suffix}`;

    guide.innerHTML = `
      <div class="mb-collection-quickview__size-guide-backdrop" data-mb-collection-size-guide-close></div>
      <div id="${panelId}" class="mb-collection-quickview__size-guide-panel size-modal" role="dialog" aria-modal="true" aria-labelledby="${titleId}" tabindex="-1">
        <div class="mb-collection-quickview__size-guide-header">
          <h3 id="${titleId}">Guía de talles</h3>
          <button type="button" class="mb-collection-quickview__size-guide-close" data-mb-collection-size-guide-close aria-label="Cerrar guía de talles">
            <i class="feather-x" aria-hidden="true"></i>
          </button>
        </div>
        <div class="mb-collection-quickview__size-guide-body" data-mb-collection-size-guide-body></div>
      </div>
    `;

    const body = guide.querySelector('[data-mb-collection-size-guide-body]');
    const clonedContent = sourceContent.cloneNode(true);
    clonedContent
      .querySelectorAll('.pop-close, [data-bs-dismiss="modal"]')
      .forEach((element) => element.remove());
    while (clonedContent.firstChild) body.append(clonedContent.firstChild);

    return guide;
  };

  const prepareShortcuts = (modalContent, sizeGuide) => {
    modalContent.querySelectorAll('.mb-product-shortcuts .grid-wrapper').forEach((wrapper) => {
      const keep = wrapper.querySelector(
        '[data-mb-measure-scroll], [data-bs-target="#size-modal"]'
      );
      if (!keep) wrapper.remove();
    });

    modalContent.querySelectorAll('[data-mb-measure-scroll]').forEach((button) => {
      button.removeAttribute('data-mb-measure-scroll');
      button.setAttribute('data-mb-collection-measure-scroll', '');
    });

    modalContent.querySelectorAll('[data-bs-target="#size-modal"]').forEach((button) => {
      button.removeAttribute('data-bs-toggle');
      button.removeAttribute('data-bs-target');
      button.setAttribute('data-mb-collection-size-guide-open', '');
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-expanded', 'false');

      const panelId = sizeGuide?.querySelector('[role="dialog"]')?.id;
      if (panelId) button.setAttribute('aria-controls', panelId);
      else button.closest('.grid-wrapper')?.remove();
    });
  };

  const openSizeGuide = (modal, opener) => {
    const guide = modal?.querySelector('[data-mb-collection-size-guide]');
    if (!guide) return;

    activeSizeGuideOpener = opener;
    opener?.setAttribute('aria-expanded', 'true');
    guide.hidden = false;
    guide.setAttribute('aria-hidden', 'false');
    guide.classList.add('is-open');
    guide.querySelector('[role="dialog"]')?.focus({ preventScroll: true });
  };

  const closeSizeGuide = (guide) => {
    if (!guide) return;

    guide.classList.remove('is-open');
    guide.setAttribute('aria-hidden', 'true');
    guide.hidden = true;

    if (activeSizeGuideOpener) {
      activeSizeGuideOpener.setAttribute('aria-expanded', 'false');
      activeSizeGuideOpener.focus({ preventScroll: true });
    }
    activeSizeGuideOpener = null;
  };

  const enhanceQuickView = async (opener) => {
    const qvHost = opener.closest('qv-modal');
    const modalSelector = qvHost?.getAttribute('data-modal');
    const modal = modalSelector ? document.querySelector(modalSelector) : null;
    const modalContent = modal?.querySelector('[id^="QuickAddInfo-"]');
    const quickViewUrl = opener.getAttribute('data-product-url');
    if (!modal || !modalContent || !quickViewUrl) return;

    const requestId = String(++requestSequence);
    modal.dataset.mbCollectionQuickview = 'true';
    modal.dataset.mbCollectionRequest = requestId;

    try {
      const productPageUrl = getProductPageUrl(quickViewUrl);
      const [markup] = await Promise.all([
        fetchProductPage(productPageUrl),
        waitForNativeQuickView(modalContent),
      ]);

      if (modal.dataset.mbCollectionRequest !== requestId) return;

      const sourceDocument = parseDocument(markup);
      const description = buildDescription(sourceDocument);
      const sizeGuide = buildSizeGuide(sourceDocument, modal);

      modal.querySelector('[data-mb-collection-size-guide]')?.remove();
      modalContent.querySelector('[data-mb-collection-product-description]')?.remove();

      prepareShortcuts(modalContent, sizeGuide);

      if (description) modalContent.append(description);
      if (sizeGuide) modal.append(sizeGuide);

      modal.querySelector('.quickview-block')?.classList.add('mb-collection-quickview__dialog');
    } catch (error) {
      console.error('Unable to enhance collection quick view:', error);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest('.collection qv-modal .quick-add-btn');
      if (opener) {
        enhanceQuickView(opener);
        return;
      }

      const sizeGuideButton = event.target.closest(
        '.quickview-modal[data-mb-collection-quickview] [data-mb-collection-size-guide-open]'
      );
      if (sizeGuideButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openSizeGuide(sizeGuideButton.closest('.quickview-modal'), sizeGuideButton);
        return;
      }

      const sizeGuideClose = event.target.closest(
        '.quickview-modal[data-mb-collection-quickview] [data-mb-collection-size-guide-close]'
      );
      if (sizeGuideClose) {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeSizeGuide(sizeGuideClose.closest('[data-mb-collection-size-guide]'));
        return;
      }

      const measureButton = event.target.closest(
        '.quickview-modal[data-mb-collection-quickview] [data-mb-collection-measure-scroll]'
      );
      if (measureButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const modal = measureButton.closest('.quickview-modal');
        const description = modal?.querySelector('[data-mb-collection-product-description]');
        const target = findMeasureTarget(description) || description;
        if (modal && target) scrollInsideQuickView(modal, target);
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const guide = document.querySelector(
        '.quickview-modal[data-mb-collection-quickview] [data-mb-collection-size-guide]:not([hidden])'
      );
      if (!guide) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      closeSizeGuide(guide);
    },
    true
  );

  document.body.addEventListener('modalClosed', () => {
    document
      .querySelectorAll('.quickview-modal[data-mb-collection-quickview] [data-mb-collection-size-guide]')
      .forEach((guide) => guide.remove());
  });
})();
